import React from 'react';

export interface DashboardCardProps {
  title?: React.ReactNode;
  /** Optional subtitle rendered under the title in the header. */
  subtitle?: React.ReactNode;
  /** Right-aligned header slot (e.g. "Sort by last contact", "Review leads"). */
  action?: React.ReactNode;
  children: React.ReactNode;
  /** Pinned to the bottom of the card via mt-auto. */
  footer?: React.ReactNode;
  /** When true, the body scrolls internally instead of growing the card. */
  scrollBody?: boolean;
  /** Span/layout classes only — never chrome overrides. */
  className?: string;
}

/**
 * The single card surface for the dashboard (LP-UX-002). Chrome — background, border, radius,
 * shadow, and padding — lives here and nowhere else. Cards stretch to equal height within a grid
 * row via `h-full`; footers pin to the bottom via `mt-auto`.
 */
export const DashboardCard: React.FC<DashboardCardProps> = ({
  title,
  subtitle,
  action,
  children,
  footer,
  scrollBody = false,
  className = '',
}) => {
  const hasHeader = title != null || subtitle != null || action != null;

  return (
    <section
      data-testid="dashboard-card"
      className={`bg-surface border border-border rounded-lg shadow-card overflow-hidden flex flex-col h-full transition-shadow hover:shadow-md ${className}`.trim()}
    >
      {hasHeader ? (
        <div className="p-6 border-b border-border flex items-start justify-between gap-2">
          <div className="min-w-0">
            {typeof title === 'string' ? (
              <h2 className="text-lg font-semibold text-fg truncate">{title}</h2>
            ) : (
              title
            )}
            {subtitle != null ? <p className="text-sm text-fg-muted mt-1">{subtitle}</p> : null}
          </div>
          {action != null ? <div className="shrink-0">{action}</div> : null}
        </div>
      ) : null}

      <div
        data-testid="dashboard-card-body"
        className={`p-6 flex-1 ${scrollBody ? 'overflow-y-auto' : ''}`.trim()}
      >
        {children}
      </div>

      {footer != null ? (
        <div className="px-6 py-4 border-t border-border mt-auto">{footer}</div>
      ) : null}
    </section>
  );
};

export interface StatCardProps {
  label: string;
  value: string | number;
  /** Clamped to two lines with a reserved height so cards stay equal regardless of caption length. */
  caption?: string;
  icon?: React.ReactNode;
  iconTintClass?: string;
  /** Span/layout classes only. */
  className?: string;
}

/** A KPI stat tile that composes DashboardCard. Caption height is reserved so value baselines align. */
export const StatCard: React.FC<StatCardProps> = ({
  label,
  value,
  caption,
  icon,
  iconTintClass = 'bg-primary-100 dark:bg-primary/20 text-primary',
  className,
}) => (
  <DashboardCard className={className}>
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-fg-muted mb-1 truncate">{label}</p>
        <p className="text-3xl font-bold text-fg tracking-tight tabular-nums">{value}</p>
        {/* Always rendered — reserved two-line height keeps 1- and 2-line captions equal. */}
        <p data-testid="stat-caption" className="text-xs text-fg-muted mt-1 min-h-10 line-clamp-2">
          {caption ?? ''}
        </p>
      </div>
      {icon ? (
        <div className={`shrink-0 w-11 h-11 rounded-md flex items-center justify-center ${iconTintClass}`}>
          {icon}
        </div>
      ) : null}
    </div>
  </DashboardCard>
);

export default DashboardCard;
